import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { t } from "@lingui/core/macro";
import { createFileRoute } from "@tanstack/react-router";

import { cn } from "@/lib/utils";
import { MetalAssetClassSelect } from "@/routes/_authenticated/trade/-components/MetalAssetClassSelect";
import { MetalProductComboBox } from "@/routes/_authenticated/trade/-components/MetalTypeComboBox";
import { NumberInput } from "@/shared/components/forms/FormattedInput";
import { Button } from "@/shared/components/ui/button";
import { Label } from "@/shared/components/ui/label";
import { formatOz } from "@/util/numberUtils";
import {
  type Metal,
  type ProductDraftLine,
  assetClassForMetal,
  isSellableMetalAsset,
  metalLabel,
  ouncesByMetal,
  resolveDraftLine,
  resolveProductLines,
} from "@/util/withdrawal";

import type { WithdrawalInputs } from "../../-components/Withdrawal/useWithdrawalInputs";
import {
  FlowTabs,
  HelpBlock,
  Screen,
  StepFooter,
  WarningBanner,
  newProductRow,
  useFlow,
  useFlowNavigate,
} from "../../-components/Withdrawal/WithdrawalFlow";

const MetalAvailability = ({
  holdings,
  requiredOz,
}: {
  holdings: WithdrawalInputs["holdings"];
  requiredOz: Record<Metal, number>;
}) => (
  <section className="@container overflow-hidden rounded-lg border" aria-labelledby="metal-availability-title">
    <div className="flex items-center justify-between gap-4 border-b bg-muted/50 px-4 py-3">
      <h3 id="metal-availability-title" className="text-sm font-semibold text-charcoal">
        {t`Available metal`}
      </h3>
    </div>
    <div className="grid divide-y @xl:grid-cols-2 @xl:divide-x @xl:divide-y-0">
      {holdings.map((holding) => {
        const required = requiredOz[holding.metal];
        const remaining = Math.max(0, holding.availableOz - required);
        const overBy = Math.max(0, required - holding.availableOz);
        const usedPercent = holding.availableOz > 0 ? Math.min(100, (required / holding.availableOz) * 100) : 0;
        return (
          <div key={holding.metal} className="p-4">
            <p className={cn("font-semibold", holding.metal === "gold" ? "text-gold" : "text-silver-dark")}>
              {metalLabel(holding.metal)}
            </p>
            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <div>
                <dt className="text-xs text-muted-foreground">{t`Selected`}</dt>
                <dd className={cn("mt-1 font-medium", overBy > 0 && "text-destructive")}>{formatOz(required)}</dd>
              </div>
              <div className="text-right">
                <dt className="text-xs text-muted-foreground">{t`Available`}</dt>
                <dd className="mt-1 font-medium">{formatOz(holding.availableOz)}</dd>
              </div>
            </dl>
            <div
              className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-label={t`${metalLabel(holding.metal)} selected`}
              aria-valuemin={0}
              aria-valuemax={holding.availableOz}
              aria-valuenow={Math.min(required, holding.availableOz)}
              aria-valuetext={t`${formatOz(required)} selected of ${formatOz(holding.availableOz)} available`}
            >
              <div
                className={cn(
                  "withdrawal-fill h-full w-full rounded-full",
                  overBy > 0 ? "bg-destructive" : "bg-primary",
                )}
                style={{ transform: `translateX(${usedPercent - 100}%)` }}
              />
            </div>
            <p className={cn("mt-2 text-xs", overBy > 0 ? "text-destructive font-medium" : "text-muted-foreground")}>
              {overBy > 0 ? t`${formatOz(overBy)} over available` : t`${formatOz(remaining)} remaining`}
            </p>
          </div>
        );
      })}
    </div>
  </section>
);

const ProductsScreen = () => {
  const navigate = useFlowNavigate();
  const { inputs, productLines, setProductLines } = useFlow();
  const lines = resolveProductLines(productLines, inputs.products);
  const requiredOz = ouncesByMetal(lines);

  const exceeded = inputs.holdings.filter((holding) => requiredOz[holding.metal] > holding.availableOz);

  const updateLine = (index: number, update: Partial<ProductDraftLine>) =>
    setProductLines(productLines.map((line, i) => (i === index ? { ...line, ...update } : line)));

  return (
    <Screen subtitle={t`Withdraw physical coins and bars.`} step={{ index: 2, total: 5 }}>
      <FlowTabs value="products" />

      <MetalAvailability holdings={inputs.holdings} requiredOz={requiredOz} />

      <section className="@container overflow-hidden rounded-lg border" aria-labelledby="product-lines-title">
        <div className="border-b bg-muted/50 px-4 py-3">
          <h3 id="product-lines-title" className="text-sm font-semibold text-charcoal">
            {t`Products to withdraw`}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {t`Select the coins or bars you want to receive from your storage balance.`}
          </p>
        </div>
        <div className="divide-y">
          {productLines.map((draft, index) => {
            const resolved = resolveDraftLine(draft, inputs.products);
            const quantityId = `product-quantity-${draft.key}`;
            return (
              <div
                key={draft.key}
                className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 p-4 @2xl:grid-cols-[120px_minmax(0,1fr)_160px_40px] @2xl:items-start"
              >
                <div className="min-w-0 @2xl:col-start-1">
                  <Label className="mb-2 block text-xs text-muted-foreground">{t`Metal`}</Label>
                  <MetalAssetClassSelect
                    value={draft.metal}
                    filter={isSellableMetalAsset}
                    onValueChange={(value) => {
                      if (value === "gold" || value === "silver") {
                        updateLine(index, { metal: value, productName: null });
                      }
                    }}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t`Remove product`}
                  className="col-start-2 row-start-1 self-end @2xl:col-start-4 @2xl:mt-6 @2xl:self-start"
                  disabled={productLines.length <= 1}
                  onClick={() => setProductLines(productLines.filter((_, i) => i !== index))}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
                <div className="col-span-2 min-w-0 @2xl:col-span-1 @2xl:col-start-2 @2xl:row-start-1">
                  <Label className="mb-2 block text-xs text-muted-foreground">{t`Product`}</Label>
                  <MetalProductComboBox
                    assetClass={assetClassForMetal(draft.metal)}
                    allowCustom={false}
                    transaction="withdrawal"
                    value={draft.productName ?? ""}
                    onValueChange={(product) => updateLine(index, { productName: product.name })}
                  />
                </div>
                <div className="min-w-0 @2xl:col-start-3 @2xl:row-start-1">
                  <Label htmlFor={quantityId} className="mb-2 block text-xs text-muted-foreground">
                    {t`Quantity`}
                  </Label>
                  <NumberInput
                    id={quantityId}
                    aria-label={t`Quantity of ${draft.productName ?? metalLabel(draft.metal)} products`}
                    allowNegative={false}
                    decimalScale={0}
                    inputMode="numeric"
                    placeholder={t`0 units`}
                    suffix={draft.quantity === 1 ? t` unit` : t` units`}
                    value={draft.quantity}
                    onValueChange={(values) =>
                      updateLine(index, {
                        quantity: values.value === "" ? "" : Math.max(0, Number(values.value)),
                      })
                    }
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    {draft.quantity === ""
                      ? t`Enter a quantity for weight`
                      : resolved
                        ? t`${formatOz(resolved.unitOz * resolved.quantity)} total`
                        : t`Select a product for weight`}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="border-t bg-muted/30 px-4 py-3">
          <Button variant="outline" size="sm" onClick={() => setProductLines([...productLines, newProductRow()])}>
            <PlusIcon className="mr-1 h-4 w-4" /> {t`Add product`}
          </Button>
        </div>
      </section>

      {exceeded.length > 0 && (
        <WarningBanner>{t`Reduce quantities to stay within your available metal.`}</WarningBanner>
      )}

      <StepFooter
        onBack={() => navigate("")}
        onContinue={() => navigate("products/shipping")}
        continueDisabled={exceeded.length > 0 || lines.length === 0}
      />
      <HelpBlock />
    </Screen>
  );
};

export const Route = createFileRoute("/_authenticated/trade/withdraw/products/")({
  component: ProductsScreen,
});
